// =====================================================================
// EGL v12 Frontend - PHOTO UPLOAD PAGE
// Mobile-optimized photo upload with 1:1 crop, zoom, and resize
// =====================================================================

import React, { useState, useRef, useCallback } from 'react';
import { useRouter } from '../lib/SmartRouter';
import Cropper from 'cropperjs';
import imageCompression from 'browser-image-compression';
import 'cropperjs/dist/cropper.css';
import './PhotoUpload.css';

type UploadStep = 'select' | 'crop' | 'uploading' | 'success';

interface UploadResult {
  success: boolean;
  s3Url?: string;
  filename?: string;
  fileSize?: number;
  error?: string;
}

export const PhotoUpload: React.FC = () => {
  const { user, navigate } = useRouter();
  
  // State
  const [step, setStep] = useState<UploadStep>('select');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageDataUrl, setImageDataUrl] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const [filenameError, setFilenameError] = useState<string>('');
  const [filenameConfirm, setFilenameConfirm] = useState<string>('');
  const [filenameConfirmError, setFilenameConfirmError] = useState<string>('');
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadResult, setUploadResult] = useState<UploadResult | null>(null);
  const [zoomLevel, setZoomLevel] = useState<number>(0);
  const [isLoadingCropper, setIsLoadingCropper] = useState<boolean>(false);
  const [cropperReady, setCropperReady] = useState<boolean>(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState<boolean>(false);
  const [stagingLocation, setStagingLocation] = useState<string>('');
  
  // Refs
  const imageRef = useRef<HTMLImageElement>(null);
  const cropperRef = useRef<Cropper | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const initAttempts = useRef<number>(0);

  // =====================================================================
  // ROBUST CROPPER INITIALIZATION
  // =====================================================================

  const initializeCropper = useCallback(() => {
    if (!imageRef.current) {
      console.warn('Image ref not available');
      return;
    }

    console.log(`Initializing cropper (attempt ${initAttempts.current + 1}/3)`);
    
    try {
      // Destroy existing cropper
      if (cropperRef.current) {
        console.log('Destroying existing cropper');
        cropperRef.current.destroy();
        cropperRef.current = null;
      }

      // Force layout reflow to ensure proper rendering
      imageRef.current.style.display = 'none';
      void imageRef.current.offsetHeight; // Trigger reflow
      imageRef.current.style.display = 'block';

      // Initialize new cropper
      cropperRef.current = new Cropper(imageRef.current, {
        aspectRatio: 1,
        viewMode: 1, // ⭐ Allow image outside canvas (recoverable)
        dragMode: 'move',
        zoomable: true,
        zoomOnWheel: false,
        zoomOnTouch: true,
        cropBoxResizable: true,
        cropBoxMovable: true,
        responsive: false, // CRITICAL: Disable auto-resize on scroll/swipe
        restore: false,
        guides: true,
        center: true,
        highlight: true,
        autoCropArea: 0.4, // ⭐ Start at 40% of canvas (reasonable default)
        minCropBoxWidth: 100,
        minCropBoxHeight: 100,
        minContainerWidth: 200,
        minContainerHeight: 200,
        minZoomRatio: 0.2, // 20% minimum zoom (reasonable limit)
        maxZoomRatio: 10, // 1000% maximum zoom (10x)
        wheelZoomRatio: 0.05,
        checkCrossOrigin: false,
        checkOrientation: true,
        modal: true,
        background: true,
        ready: () => {
          console.log('✅ Cropper ready!');
          setCropperReady(true);
          setIsLoadingCropper(false);
          setZoomLevel(0);
          initAttempts.current = 0;
          
          // Force a re-render after ready
          setTimeout(() => {
            if (cropperRef.current) {
              cropperRef.current.crop();
            }
          }, 100);
        },
        zoom: (event) => {
          const ratio = event.detail.ratio;
          setZoomLevel(Math.round((ratio - 1) * 100));
        },
        cropstart: () => {
          console.log('Crop started');
        },
        cropmove: () => {
          // Prevent excessive logging
        },
        cropend: () => {
          console.log('Crop ended');
        }
      });

      console.log('Cropper instance created');
      
    } catch (error) {
      console.error('Cropper initialization error:', error);
      initAttempts.current++;
      
      if (initAttempts.current < 3) {
        console.log('Retrying initialization...');
        setTimeout(initializeCropper, 500);
      } else {
        setIsLoadingCropper(false);
        alert('Failed to initialize image cropper. Please try selecting the image again.');
      }
    }
  }, []);

  // =====================================================================
  // STEP 1: FILE SELECTION / CAMERA CAPTURE
  // =====================================================================

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 20MB for input)
    if (file.size > 20 * 1024 * 1024) {
      alert('Image is too large. Maximum size is 20MB.');
      return;
    }

    setIsLoadingCropper(true);
    setCropperReady(false);
    initAttempts.current = 0;

    try {
      console.log('📸 Original image size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      
      // ⭐ PREPROCESS IMAGE: Fix iOS orientation + Resize for performance
      const compressionOptions = {
        maxSizeMB: 2,
        maxWidthOrHeight: 2000,  // Resize large images for smoother mobile performance
        useWebWorker: true,
        fileType: 'image/jpeg',
        initialQuality: 0.95,
        exifOrientation: 1  // Auto-correct EXIF orientation (fixes iOS cropped images)
      };

      console.log('🔄 Preprocessing image (iOS fix + resize)...');
      const processedFile = await imageCompression(file, compressionOptions);
      
      console.log('✅ Preprocessed:', (processedFile.size / 1024 / 1024).toFixed(2), 'MB');
      
      setSelectedFile(processedFile);

      // Read preprocessed file as data URL
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        setImageDataUrl(dataUrl);
        setStep('crop');
        setZoomLevel(0);
        
        console.log('Image data loaded, waiting for DOM update...');
        
        // Wait for image element to be in DOM and fully loaded
        setTimeout(() => {
          if (imageRef.current) {
            // Wait for image to actually load before initializing cropper
            const img = imageRef.current;
            
            if (img.complete && img.naturalHeight !== 0) {
              // Image already loaded (cached)
              console.log('Image already loaded, initializing cropper');
              initializeCropper();
            } else {
              // Wait for image to load
              console.log('Waiting for image to load...');
              img.onload = () => {
                console.log('Image loaded successfully');
                // Additional delay for mobile browsers
                setTimeout(initializeCropper, 300);
              };
              img.onerror = () => {
                console.error('Image failed to load');
                setIsLoadingCropper(false);
                alert('Failed to load image. Please try again.');
              };
            }
          } else {
            console.warn('Image ref not found in DOM');
            setIsLoadingCropper(false);
          }
        }, 100);
      };
      
      reader.onerror = () => {
        console.error('FileReader error');
        setIsLoadingCropper(false);
        alert('Failed to read image file. Please try again.');
      };
      
      reader.readAsDataURL(processedFile);
      
    } catch (error) {
      console.error('❌ Image preprocessing error:', error);
      setIsLoadingCropper(false);
      alert('Failed to process image. Please try again.');
    }
  }, [initializeCropper]);

  // =====================================================================
  // STEP 2: FILENAME VALIDATION
  // =====================================================================

  const handleFilenameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // 1. Convert to uppercase
    value = value.toUpperCase();
    
    // 2. Remove invalid characters (keep only A-Z, 0-9, and -)
    value = value.replace(/[^A-Z0-9-]/g, '');
    
    // 3. Limit to 22 characters
    value = value.substring(0, 22);
    
    // 4. Validation
    if (value.length === 0) {
      setFilenameError('Filename is required');
    } else if (value.length < 3) {
      setFilenameError('Minimum 3 characters');
    } else if (value.startsWith('-') || value.endsWith('-')) {
      setFilenameError('Cannot start or end with hyphen');
    } else if (value.includes('--')) {
      setFilenameError('No consecutive hyphens allowed');
    } else {
      setFilenameError('');
    }
    
    setFilename(value);
    
    // Clear confirmation when filename changes
    if (filenameConfirm) {
      setFilenameConfirm('');
      setFilenameConfirmError('Please re-confirm filename');
    }
  };

  // Handle confirmation filename input (with paste prevention)
  const handleFilenameConfirmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // 1. Convert to uppercase (same as original)
    value = value.toUpperCase();
    
    // 2. Remove invalid characters (same as original)
    value = value.replace(/[^A-Z0-9-]/g, '');
    
    // 3. Limit to 22 characters (same as original)
    value = value.substring(0, 22);
    
    setFilenameConfirm(value);
    
    // Validate match
    if (value.length === 0) {
      setFilenameConfirmError('Please confirm filename');
    } else if (value !== filename) {
      setFilenameConfirmError('Filenames do not match');
    } else {
      setFilenameConfirmError('');
    }
  };

  // Prevent paste in confirmation field
  const handleConfirmPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
  };

  // Get character-by-character match status for real-time feedback
  const getCharacterMatchStatus = (): { matches: boolean[]; allMatch: boolean } => {
    const matches: boolean[] = [];
    for (let i = 0; i < filenameConfirm.length; i++) {
      matches.push(i < filename.length && filenameConfirm[i] === filename[i]);
    }
    const allMatch = filenameConfirm.length > 0 && 
                     filenameConfirm.length === filename.length && 
                     filenameConfirm === filename;
    return { matches, allMatch };
  };

  // =====================================================================
  // ZOOM CONTROLS
  // =====================================================================

  const handleZoomIn = () => {
    if (cropperRef.current) {
      cropperRef.current.zoom(0.1);  // Zoom in by 10%
    }
  };

  const handleZoomOut = () => {
    if (cropperRef.current) {
      cropperRef.current.zoom(-0.1);  // Zoom out by 10%
    }
  };

  const handleResetZoom = () => {
    if (cropperRef.current) {
      cropperRef.current.reset();  // Reset to initial state
      setZoomLevel(0);
    }
  };

  const handleRotateLeft = () => {
    if (cropperRef.current) {
      cropperRef.current.rotate(-90);
    }
  };

  const handleRotateRight = () => {
    if (cropperRef.current) {
      cropperRef.current.rotate(90);
    }
  };

  // =====================================================================
  // DIRECTIONAL MOVEMENT CONTROLS (8-WAY D-PAD)
  // =====================================================================

  const MOVE_STEP = 20; // Move 20px per tap

  const handleMoveUp = () => {
    if (cropperRef.current) {
      cropperRef.current.move(0, -MOVE_STEP);
    }
  };

  const handleMoveDown = () => {
    if (cropperRef.current) {
      cropperRef.current.move(0, MOVE_STEP);
    }
  };

  const handleMoveLeft = () => {
    if (cropperRef.current) {
      cropperRef.current.move(-MOVE_STEP, 0);
    }
  };

  const handleMoveRight = () => {
    if (cropperRef.current) {
      cropperRef.current.move(MOVE_STEP, 0);
    }
  };

  const handleMoveUpLeft = () => {
    if (cropperRef.current) {
      cropperRef.current.move(-MOVE_STEP, -MOVE_STEP);
    }
  };

  const handleMoveUpRight = () => {
    if (cropperRef.current) {
      cropperRef.current.move(MOVE_STEP, -MOVE_STEP);
    }
  };

  const handleMoveDownLeft = () => {
    if (cropperRef.current) {
      cropperRef.current.move(-MOVE_STEP, MOVE_STEP);
    }
  };

  const handleMoveDownRight = () => {
    if (cropperRef.current) {
      cropperRef.current.move(MOVE_STEP, MOVE_STEP);
    }
  };

  const handleCenterImage = () => {
    if (cropperRef.current) {
      // Reset to center position while maintaining zoom
      const canvasData = cropperRef.current.getCanvasData();
      const containerData = cropperRef.current.getContainerData();
      
      cropperRef.current.moveTo(
        (containerData.width - canvasData.naturalWidth) / 2,
        (containerData.height - canvasData.naturalHeight) / 2
      );
    }
  };

  // =====================================================================
  // STEP 3: UPLOAD PROCESS
  // =====================================================================

  const handleUpload = async (forceOverwrite: boolean = false) => {
    console.log('📤 [UPLOAD] Starting upload, forceOverwrite:', forceOverwrite);
    
    // Validate filename
    if (!filename || filenameError) {
      alert('Please enter a valid filename');
      return;
    }

    if (!cropperRef.current) {
      alert('Cropper not initialized');
      return;
    }

    setStep('uploading');
    setUploadProgress(0);

    try {
      // 1. Get cropped canvas (1000x1000)
      const canvas = cropperRef.current.getCroppedCanvas({
        width: 1000,
        height: 1000,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high'
      });

      // 2. Convert to JPG blob (90% quality)
      const blob: Blob = await new Promise((resolve) => {
        canvas.toBlob(
          (blob) => resolve(blob as Blob),
          'image/jpeg',
          0.90  // 90% quality
        );
      });

      console.log('Original file size:', selectedFile?.size, 'bytes');
      console.log('Compressed JPG size:', blob.size, 'bytes');

      setUploadProgress(20);

      // 3. Request presigned URL from backend (with duplicate check)
      const token = localStorage.getItem('egl_token');
      console.log('📤 [UPLOAD] Requesting presigned URL, forceOverwrite:', forceOverwrite);
      
      const presignedController = new AbortController();
      const presignedTimeout = setTimeout(() => presignedController.abort(), 30000);
      let presignedResponse: Response;
      try {
        presignedResponse = await fetch('/api/s3/staging-presigned-url', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            filename: `${filename}.jpg`,
            contentType: 'image/jpeg',
            forceOverwrite: forceOverwrite
          }),
          signal: presignedController.signal
        });
      } finally {
        clearTimeout(presignedTimeout);
      }

      console.log('📥 [UPLOAD] Presigned URL response status:', presignedResponse.status);

      // Handle duplicate file (409 Conflict)
      if (presignedResponse.status === 409) {
        console.log('⚠️ [UPLOAD] DUPLICATE FILE DETECTED!');
        const duplicateData = await presignedResponse.json();
        
        // Save staging location and show modal
        setStagingLocation(duplicateData.data.stagingFolder || `EGL-${user?.location}-Staging`);
        setShowDuplicateModal(true);
        // ⭐ DON'T change step - keep crop locked, modal overlays
        setUploadProgress(0); // Reset progress bar
        return; // Stop upload
      }

      if (!presignedResponse.ok) {
        const errorData = await presignedResponse.json();
        throw new Error(errorData.message || 'Failed to get upload URL');
      }

      const presignedData = await presignedResponse.json();
      const { presignedUrl, s3Url, data: uploadData } = presignedData;
      
      // Save staging location from backend response
      if (uploadData && uploadData.stagingFolder) {
        setStagingLocation(uploadData.stagingFolder);
      }
      
      setUploadProgress(40);

      // 4. Upload to S3 using XMLHttpRequest (for progress tracking)
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const percent = 40 + (e.loaded / e.total) * 50; // 40% to 90%
            setUploadProgress(Math.round(percent));
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });


        // 60-second timeout for S3 upload
        xhr.timeout = 60000;

        xhr.addEventListener('timeout', () => {
          reject(new Error('Upload timed out. Please try again.'));
        });

        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', 'image/jpeg');
        xhr.send(blob);
      });

      setUploadProgress(100);

      // 5. Success!
      setUploadResult({
        success: true,
        s3Url,
        filename: `${filename}.jpg`,
        fileSize: blob.size
      });
      setStep('success');

    } catch (error) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed'
      });
      setStep('success'); // Show error in success step
    }
  };

  // =====================================================================
  // RESET / CANCEL
  // =====================================================================

  const handleReset = () => {
    if (cropperRef.current) {
      cropperRef.current.destroy();
      cropperRef.current = null;
    }
    setStep('select');
    setSelectedFile(null);
    setImageDataUrl('');
    setFilename('');
    setFilenameError('');
    setFilenameConfirm('');
    setFilenameConfirmError('');
    setUploadProgress(0);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleCancel = () => {
    if (confirm('Cancel upload? Your photo and filename will be lost.')) {
      handleReset();
    }
  };

  // =====================================================================
  // RENDER
  // =====================================================================

  return (
    <div className="photo-upload-container">
      {/* Header */}
      <div className="photo-upload-header">
        <div className="header-left">
          <h2>📷 Upload Images</h2>
          <p className="upload-subtitle">
            Upload location: <strong>{user?.location || 'Unknown'}</strong>
          </p>
        </div>
        
        <div className="header-right">
          <button 
            className="btn-back-dashboard"
            onClick={() => navigate('/dashboard')}
            title="Back to Dashboard"
          >
            🏠 Dashboard
          </button>
          <button
            className="btn-logout"
            onClick={() => {
              localStorage.removeItem('egl_token');
              localStorage.removeItem('user');
              navigate('/login');
            }}
            title="Logout"
          >
            🚪 Logout
          </button>
        </div>
      </div>

      {/* STEP 1: SELECT PHOTO */}
      {step === 'select' && (
        <div className="upload-step select-step">
          <div className="instructions">
            <h3>Take or Select Photo</h3>
            <ul>
              <li>📱 Use your phone camera or choose from gallery</li>
              <li>📐 You'll crop it to a perfect square (1:1)</li>
              <li>🔍 Zoom and resize as needed</li>
              <li>💾 Final image: 1000x1000 pixels, JPG format</li>
            </ul>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleFileSelect}
            className="file-input"
            id="photo-input"
          />
          <label htmlFor="photo-input" className="file-input-label">
            📷 Take Photo / Choose File
          </label>

          <p className="file-hint">
            Accepts: JPG, PNG, HEIC, WebP (max 20MB)
          </p>
        </div>
      )}

      {/* STEP 2: CROP PHOTO */}
      {step === 'crop' && (
        <div className="upload-step crop-step">
          <div className="crop-instructions">
            <h3>Crop & Adjust Photo</h3>
            <div className="crop-hints">
              <span>🔍 Pinch to Zoom</span>
              <span>👆 Drag to Move</span>
              <span>↔️ Resize Crop Box</span>
            </div>
          </div>

          <div className="crop-area" style={{ minHeight: cropperReady ? 'auto' : '400px' }}>
            {!cropperReady && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Loading cropper...</p>
              </div>
            )}
            <div style={{ opacity: cropperReady ? 1 : 0, transition: 'opacity 0.3s ease' }}>
              <img
                ref={imageRef}
                src={imageDataUrl}
                alt="Crop preview"
                style={{ maxWidth: '100%', display: 'block' }}
              />
            </div>
          </div>

          {/* Zoom Controls */}
          {cropperReady && (
            <div className="zoom-controls">
              <button 
                className="zoom-btn"
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                ➖
              </button>
              <button 
                className="zoom-btn reset-btn"
                onClick={handleResetZoom}
                title="Reset View"
              >
                🔄 Reset
              </button>
              <button 
                className="zoom-btn"
                onClick={handleZoomIn}
                title="Zoom In"
              >
                ➕
              </button>
              <button 
                className="zoom-btn rotate-btn"
                onClick={handleRotateLeft}
                title="Rotate Left"
              >
                ↺
              </button>
              <button 
                className="zoom-btn rotate-btn"
                onClick={handleRotateRight}
                title="Rotate Right"
              >
                ↻
              </button>
            </div>
          )}

          {/* 8-Way Directional Pad */}
          {cropperReady && (
            <div className="dpad-controls">
              <div className="dpad-label">Move Image (20px per tap)</div>
              <div className="dpad-grid">
                <button 
                  className="dpad-btn dpad-tl"
                  onClick={handleMoveUpLeft}
                  title="Move Up-Left"
                >
                  ↖
                </button>
                <button 
                  className="dpad-btn dpad-t"
                  onClick={handleMoveUp}
                  title="Move Up"
                >
                  ↑
                </button>
                <button 
                  className="dpad-btn dpad-tr"
                  onClick={handleMoveUpRight}
                  title="Move Up-Right"
                >
                  ↗
                </button>
                
                <button 
                  className="dpad-btn dpad-l"
                  onClick={handleMoveLeft}
                  title="Move Left"
                >
                  ←
                </button>
                <button 
                  className="dpad-btn dpad-center"
                  onClick={handleCenterImage}
                  title="Center Image"
                >
                  ⊕
                </button>
                <button 
                  className="dpad-btn dpad-r"
                  onClick={handleMoveRight}
                  title="Move Right"
                >
                  →
                </button>
                
                <button 
                  className="dpad-btn dpad-bl"
                  onClick={handleMoveDownLeft}
                  title="Move Down-Left"
                >
                  ↙
                </button>
                <button 
                  className="dpad-btn dpad-b"
                  onClick={handleMoveDown}
                  title="Move Down"
                >
                  ↓
                </button>
                <button 
                  className="dpad-btn dpad-br"
                  onClick={handleMoveDownRight}
                  title="Move Down-Right"
                >
                  ↘
                </button>
              </div>
            </div>
          )}

          <div className="filename-input-section">
            <label htmlFor="filename-input">
              Filename (3-22 characters, uppercase, A-Z 0-9 and - only):
            </label>
            <div className="filename-input-wrapper">
              <input
                id="filename-input"
                type="text"
                value={filename}
                onChange={handleFilenameChange}
                placeholder="ENTER-FILENAME-HERE"
                maxLength={22}
                className={`filename-input ${filenameError ? 'error' : ''}`}
                autoCapitalize="characters"
                inputMode="text"
              />
              <span className="char-counter">{filename.length}/22</span>
            </div>
            {filenameError && (
              <span className="filename-error">⚠️ {filenameError}</span>
            )}
            {!filenameError && filename.length >= 3 && (
              <span className="filename-success">
                ✅ Will save as: <strong>{filename}.jpg</strong> in{' '}
                <strong>EGL-{user?.location}-Staging/</strong>
              </span>
            )}
            
            {/* CONFIRMATION FIELD - Progressive reveal after valid filename */}
            {!filenameError && filename.length >= 3 && (
              <div className="filename-confirm-section">
                <label htmlFor="filename-confirm-input">
                  Confirm Filename (re-type to verify):
                </label>
                <div className="filename-input-wrapper">
                  <input
                    id="filename-confirm-input"
                    type="text"
                    value={filenameConfirm}
                    onChange={handleFilenameConfirmChange}
                    onPaste={handleConfirmPaste}
                    placeholder="RE-TYPE FILENAME"
                    maxLength={22}
                    className={`filename-input ${filenameConfirmError ? 'error' : filenameConfirm === filename && filename.length > 0 ? 'success' : ''}`}
                    autoCapitalize="characters"
                    inputMode="text"
                    autoComplete="off"
                  />
                  <span className="char-counter">{filenameConfirm.length}/22</span>
                </div>
                
                {/* Real-time character-by-character match feedback */}
                {filenameConfirm.length > 0 && (
                  <div className="character-match-feedback">
                    <span className="match-label">Match: </span>
                    <span className="match-chars">
                      {(() => {
                        const { matches } = getCharacterMatchStatus();
                        return filenameConfirm.split('').map((char, idx) => (
                          <span 
                            key={idx} 
                            className={`match-char ${matches[idx] ? 'match' : 'mismatch'}`}
                          >
                            {matches[idx] ? '✓' : '✗'}
                          </span>
                        ));
                      })()}
                    </span>
                    {filenameConfirm.length < filename.length && (
                      <span className="remaining-chars">
                        ({filename.length - filenameConfirm.length} more)
                      </span>
                    )}
                  </div>
                )}
                
                {filenameConfirmError && filenameConfirm.length > 0 && (
                  <span className="filename-error">⚠️ {filenameConfirmError}</span>
                )}
                {!filenameConfirmError && filenameConfirm === filename && filename.length >= 3 && (
                  <span className="filename-success">
                    ✅ Filenames match! Will save as: <strong>{filename}.jpg</strong> in{' '}
                    <strong>EGL-{user?.location}-Staging/</strong>
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="crop-actions">
            <button 
              className="btn-secondary"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button 
              className="btn-primary"
              onClick={() => handleUpload(false)}
              disabled={!filename || !!filenameError || filename.length < 3 || filenameConfirm !== filename}
            >
              Upload ✓
            </button>
          </div>
        </div>
      )}

      {/* STEP 3: UPLOADING */}
      {step === 'uploading' && (
        <div className="upload-step uploading-step">
          <h3>Uploading...</h3>
          <div className="progress-container">
            <progress value={uploadProgress} max="100" />
            <span className="progress-text">{uploadProgress}%</span>
          </div>
          <p className="upload-status">
            {uploadProgress < 40 && '📤 Preparing upload...'}
            {uploadProgress >= 40 && uploadProgress < 90 && '☁️ Uploading to S3...'}
            {uploadProgress >= 90 && '✅ Finalizing...'}
          </p>
        </div>
      )}

      {/* STEP 4: SUCCESS / ERROR */}
      {step === 'success' && uploadResult && (
        <div className="upload-step success-step">
          {uploadResult.success ? (
            <>
              <div className="success-icon">✅</div>
              <h3>Upload Successful!</h3>
              <div className="upload-details">
                <p><strong>Filename:</strong> {uploadResult.filename}</p>
                <p><strong>Location:</strong> {stagingLocation || `EGL-${user?.location}-Staging`}/</p>
                <p><strong>Size:</strong> {((uploadResult.fileSize || 0) / 1024).toFixed(0)} KB</p>
                <p><strong>Format:</strong> JPG (1000x1000px)</p>
                <p className="s3-url-label"><strong>S3 URL:</strong></p>
                <code className="s3-url">{uploadResult.s3Url}</code>
              </div>
              <div className="success-actions">
                <button 
                  className="btn-primary"
                  onClick={handleReset}
                >
                  Upload Another Photo
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="error-icon">❌</div>
              <h3>Upload Failed</h3>
              <p className="error-message">{uploadResult.error}</p>
              <div className="error-actions">
                <button 
                  className="btn-primary"
                  onClick={handleReset}
                >
                  Try Again
                </button>
                <button 
                  className="btn-secondary"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Duplicate File Modal */}
      {showDuplicateModal && (
        <div className="modal-overlay" onClick={() => setShowDuplicateModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>⚠️ File Already Exists</h3>
            <p className="modal-message">
              A file named <strong>{filename}.jpg</strong> already exists in <strong>{stagingLocation}/</strong>
            </p>
            <p className="modal-question">What would you like to do?</p>
            <div className="modal-actions">
              <button
                className="btn-secondary modal-btn"
                onClick={() => {
                  setShowDuplicateModal(false);
                  setStep('crop'); // Go back to crop step (crop is preserved!)
                  // Cropper is already initialized, crop is locked
                }}
              >
                ✏️ Rename
              </button>
              <button
                className="btn-warning modal-btn"
                onClick={() => {
                  setShowDuplicateModal(false);
                  if (confirm(`Are you sure you want to overwrite ${filename}.jpg?`)) {
                    handleUpload(true); // Force overwrite
                  }
                }}
              >
                🔄 Overwrite
              </button>
              <button
                className="btn-cancel modal-btn"
                onClick={() => setShowDuplicateModal(false)}
              >
                ✖ Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

